import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ClipboardList, Download, Filter, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import HdjImportModal from "@/components/secretary/HdjImportModal";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";

const SOURCE_LABELS = {
  alert: "Alerte",
  oral: "Observance",
  prechemo: "Pré-chimio",
  pro: "PRO",
  transmission: "Transmission",
  escalation: "Médecin",
  doctor_order: "Ordre médecin",
  hdj_import: "Import HDJ",
  appointment_request: "RDV",
  secretary: "Secrétariat",
  status_change: "Statut",
  manual: "Tâche",
};

const SECRETARIAT_SOURCES = ["appointment_request", "secretary", "doctor_order", "manual"];

const AUDIENCE_COPY = {
  IDE: { kicker: "Relève partagée", title: "À faire", pdf: "PDF relève", file: "hana-releve-ide.pdf", empty: "Aucune tâche en attente", testid: "nurse-workqueue-page" },
  SECRETARIAT: { kicker: "Carnet de bord secrétariat", title: "Missions", pdf: "PDF carnet", file: "hana-carnet-secretariat.pdf", empty: "Aucune mission en attente", testid: "secretary-workqueue-page" },
};

const PRIORITY_STYLES = {
  URGENT: "bg-rose-50 text-rose-700 border-rose-200",
  HIGH: "bg-amber-50 text-amber-700 border-amber-200",
  NORMAL: "bg-emerald-50 text-emerald-700 border-emerald-200",
  LOW: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function NurseWorkQueue({ audience = "IDE" }) {
  const copy = AUDIENCE_COPY[audience] || AUDIENCE_COPY.IDE;
  const visibleSources = audience === "SECRETARIAT" ? SECRETARIAT_SOURCES : Object.keys(SOURCE_LABELS);
  const [items, setItems] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState("");

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clinical-workqueue", { params: { audience } });
      setItems(data.items || []);
      setSummary(data.summary || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de charger la relève");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQueue(); }, [audience]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredItems = useMemo(() => (
    sourceFilter ? items.filter(item => item.sourceType === sourceFilter) : items
  ), [items, sourceFilter]);

  const closeItem = async (item) => {
    try {
      await api.patch(`/clinical-workqueue/tasks/${encodeURIComponent(item.id)}/status`, { status: "DONE" });
      toast.success("Élément marqué comme vu/fait");
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action impossible");
    }
  };

  const assignItem = async (item) => {
    try {
      await api.patch(`/clinical-workqueue/tasks/${encodeURIComponent(item.id)}/status`, { status: "IN_PROGRESS" });
      toast.success("Élément pris en charge");
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assignation impossible");
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/clinical-workqueue/handover.pdf", { responseType: "blob", params: { audience } });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = copy.file;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export PDF impossible");
    }
  };

  return (
    <ProfessionalLayout>
      {showImport && <HdjImportModal onClose={() => setShowImport(false)} onDone={fetchQueue} />}
      <div className="space-y-6" data-testid={copy.testid}>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <p className="text-sm text-emerald-700 font-semibold">{copy.kicker}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-slate-900 tracking-normal">{copy.title}</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={fetchQueue} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="workqueue-refresh-button">
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
            <button onClick={exportPdf} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800" data-testid="workqueue-export-pdf-button">
              <Download className="w-4 h-4" /> {copy.pdf}
            </button>
          {audience === "SECRETARIAT" && (
            <button onClick={() => setShowImport(true)} data-testid="hdj-import-open" className="flex items-center gap-2 bg-emerald-600 text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-emerald-700">
              Importer planning HDJ
            </button>
          )}
          </div>
        </div>

        <div className={`grid grid-cols-2 md:grid-cols-5 ${visibleSources.length > 5 ? "xl:grid-cols-10" : ""} gap-3`} data-testid="workqueue-summary">
          {visibleSources.map((key) => [key, SOURCE_LABELS[key]]).map(([key, label]) => (
            <button key={key} onClick={() => setSourceFilter(sourceFilter === key ? "" : key)} className={`text-left p-4 rounded-lg border ${sourceFilter === key ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid={`workqueue-filter-${key}`}>
              <p className="text-xs text-slate-500 flex items-center gap-1"><Filter className="w-3 h-3" /> {label}</p>
              <p className="text-2xl font-bold text-slate-900">{summary[key] || 0}</p>
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="workqueue-list">
          {loading ? (
            <div className="p-8 text-center text-slate-500" data-testid="workqueue-loading">Chargement...</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-10 text-center" data-testid="workqueue-empty-state">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
              <p className="font-semibold text-slate-800">{copy.empty}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredItems.map((item) => (
                <div key={item.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`workqueue-item-${item.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-1 rounded-full border ${PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.NORMAL}`} data-testid={`workqueue-item-priority-${item.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`}>{item.priority}</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">{SOURCE_LABELS[item.sourceType] || item.sourceType}</span>
                      </div>
                      <p className="font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> {item.title}</p>
                      <p className="text-sm text-slate-600 mt-1">{item.patientName} · {item.description}</p>
                      <p className="text-xs text-slate-400 mt-1">Échéance : {item.dueAt || "non définie"}</p>
                    </div>
                    <div className="flex gap-2">
                      {item.actionUrl && <Link to={item.actionUrl} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-white" data-testid={`workqueue-open-${item.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`}>Ouvrir</Link>}
                      <button onClick={() => assignItem(item)} className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`workqueue-assign-${item.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`}>Prendre</button>
                      <button onClick={() => closeItem(item)} className="px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800" data-testid={`workqueue-done-${item.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`}>Vu / fait</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ProfessionalLayout>
  );
}
